import {clsx} from 'clsx'
import {Tooltip} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import {memo, use} from 'react'
import type {AnchorHTMLAttributes, FocusEvent, MouseEvent, ReactNode, Ref} from 'react'
import {LayoutLink as Link} from '../layout-runtime'
import {useCollapsibleSidebarContext} from '../CollapsibleSidebar/CollapsibleSidebar'
import {GroupedContext} from './sidebar-context'
import {getCurrentState, sidebarChildKind} from './contains-current'
import {SidebarAction} from './Action'
import actionStyles from './Action.module.css'
import styles from './Item.module.css'
import rowStyles from './Row.module.css'

type SidebarItemLinkProps =
  | {href: string; to?: never; forceHardNavigation?: never}
  | {to: string; href?: never; forceHardNavigation?: boolean}

export type ItemProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> &
  SidebarItemLinkProps & {
    children: ReactNode
    leadingVisual?: ReactNode
    trailingVisual?: ReactNode
    trailingAction?: ReactNode
    actionVisibility?: 'always' | 'hover'
    tooltipText?: string
    current?: boolean
    ref?: Ref<HTMLAnchorElement>
  }

type ConsumerHandlers = Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'onFocus' | 'onMouseEnter' | 'onMouseOverCapture'>

// Keep consumer handlers out of Tooltip's cloning and delayed event forwarding.
function ItemLink({
  children,
  consumerHandlers,
  onFocus,
  onMouseOverCapture,
  to,
  forceHardNavigation,
  href,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  consumerHandlers: ConsumerHandlers
  to?: string
  forceHardNavigation?: boolean
  ref?: Ref<HTMLAnchorElement>
}) {
  const anchorProps = {
    ...props,
    onFocus: (event: FocusEvent<HTMLAnchorElement>) => {
      consumerHandlers.onFocus?.(event)
      onFocus?.(event)
    },
    onMouseEnter: consumerHandlers.onMouseEnter,
    onMouseOverCapture: (event: MouseEvent<HTMLAnchorElement>) => {
      consumerHandlers.onMouseOverCapture?.(event)
      onMouseOverCapture?.(event)
    },
  }
  return to && !forceHardNavigation ? (
    <Link {...anchorProps} to={to}>
      {children}
    </Link>
  ) : (
    <a {...anchorProps} href={href ?? to}>
      {children}
    </a>
  )
}

export const Item = Object.assign(
  memo(function SidebarItem({
    children,
    href,
    to,
    forceHardNavigation,
    leadingVisual,
    trailingVisual,
    trailingAction,
    actionVisibility = 'always',
    tooltipText,
    current,
    'aria-current': ariaCurrent,
    ref,
    className,
    onFocus,
    onMouseEnter,
    onMouseOverCapture,
    ...linkProps
  }: ItemProps) {
    const {visualIsExpanded} = useCollapsibleSidebarContext()
    const grouped = use(GroupedContext)
    const currentState = getCurrentState({current, 'aria-current': ariaCurrent})
    if (!grouped && !leadingVisual) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Sidebar.Item] Items outside a SectionGroup or Accordion need leadingVisual to appear in the rail. This item is only shown when the sidebar is expanded.',
        )
      }
      if (!visualIsExpanded) return null
    }
    const content = (
      <>
        {leadingVisual ? (
          <span className={clsx(styles.LeadingVisual, !grouped && rowStyles.Icon)}>{leadingVisual}</span>
        ) : null}
        <span
          data-sidebar-collapse-content
          className={clsx(rowStyles.Label, !visualIsExpanded && styles.visuallyHidden)}
        >
          {children}
        </span>
        {visualIsExpanded && trailingVisual ? (
          <span data-sidebar-collapse-content className={styles.TrailingVisual}>
            {trailingVisual}
          </span>
        ) : null}
      </>
    )
    const anchorProps = {
      ...linkProps,
      ref,
      'aria-current': currentState.ariaCurrent,
      className: clsx(
        rowStyles.Row,
        styles.Link,
        currentState.current && rowStyles.current,
        grouped && !leadingVisual && rowStyles.textLed,
        className,
      ),
    }
    const link = (
      <ItemLink
        {...anchorProps}
        href={href}
        to={to}
        forceHardNavigation={forceHardNavigation}
        consumerHandlers={{onFocus, onMouseEnter, onMouseOverCapture}}
      >
        {content}
      </ItemLink>
    )
    return (
      <div
        className={clsx(styles.Item, actionStyles.actionRow, currentState.current && styles.current)}
        data-component="Sidebar.Item"
      >
        {tooltipText || typeof children === 'string' ? (
          <FeatureFlags flags={{primer_react_merged_forwarded_refs: true}}>
            <Tooltip
              text={visualIsExpanded ? '' : (tooltipText ?? (children as string))}
              direction="e"
              delay="medium"
              _privateDisableTooltip={visualIsExpanded}
              popover={visualIsExpanded ? undefined : 'auto'}
            >
              {link}
            </Tooltip>
          </FeatureFlags>
        ) : (
          link
        )}
        {visualIsExpanded && trailingAction ? (
          <SidebarAction hover={actionVisibility === 'hover'}>{trailingAction}</SidebarAction>
        ) : null}
      </div>
    )
  }),
  {[sidebarChildKind]: 'item' as const},
)
